import React, { useState, useEffect, useCallback } from 'react';
import { makeAuthenticatedRequest } from '../core/api/client';

const GRADE_COLORS = {
  '1.4021':'#3266ad','1.4057':'#1D9E75','1.4005':'#D85A30','1.4307':'#D4537E',
  '1.4305':'#BA7517','1.4104':'#888780','1.4404':'#534AB7','1.4828':'#0F6E56',
  '1.4571':'#993556','303':'#E07820','420':'#185FA5','410':'#3B6D11',
  '304':'#993C1D','316L':'#3C3489','305':'#854F0B','431':'#533AB7',
};
const getColor = (g) => GRADE_COLORS[g] || '#888780';

const PERIODS = [
  { label: '1M', value: '1M', desc: 'Last 1 month' },
  { label: '3M', value: '3M', desc: 'Last 3 months' },
  { label: '6M', value: '6M', desc: 'Last 6 months' },
  { label: '1Y', value: '1Y', desc: 'Last 12 months' },
  { label: '3Y', value: '3Y', desc: 'Last 3 years' },
];

const VIEWS = [
  { label: 'Invoice volume', value: 'volume' },
  { label: 'Monthly trend', value: 'trend' },
  { label: 'Customers', value: 'customers' },
  { label: 'Enquiry heatmap', value: 'heatmap' },
];

export default function DemandForecastPage() {
  const [period, setPeriod] = useState('1Y');
  const [view, setView] = useState('volume');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailCell, setDetailCell] = useState(null);
  const [detailRows, setDetailRows] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);

  const fetchData = useCallback((p) => {
    setLoading(true);
    makeAuthenticatedRequest(`/api/v1/enquiries/demand-forecast?period=${p}`)
      .then(r => r.json())
      .then(json => setData(json))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchData(period); }, [period, fetchData]);

  const openDetail = async (grade, month) => {
    setDetailCell({ grade, month });
    setDetailLoading(true);
    setDetailRows([]);
    try {
      const rows = await makeAuthenticatedRequest(
        `/api/v1/enquiries/by-grade-month?grade=${encodeURIComponent(grade)}&month=${encodeURIComponent(month)}`
      ).then(r => r.json());
      setDetailRows(Array.isArray(rows) ? rows : []);
    } catch(e) { console.error(e); }
    finally { setDetailLoading(false); }
  };

  const invoiceMonths = data ? [...new Set(data.invoice_data?.map(d => d.month))].sort() : [];
  const invoiceGrades = data ? [...new Set(data.invoice_data?.map(d => d.grade))].sort((a,b) => {
    const ta = data.grades?.find(g=>g.grade===a)?.total_mt || 0;
    const tb = data.grades?.find(g=>g.grade===b)?.total_mt || 0;
    return tb - ta;
  }) : [];

  const getInvMT = (grade, month) => {
    const r = data?.invoice_data?.find(d => d.grade===grade && d.month===month);
    return r ? r.total_mt : 0;
  };
  const getEnqCount = (grade, month) => {
    const r = data?.enquiry_data?.find(d => d.grade===grade && d.month===month);
    return r ? r.count : 0;
  };

  const totalMT = data?.grades?.reduce((s,g) => s + g.total_mt, 0) || 0;
  const avgMonthly = invoiceMonths.length > 0 ? totalMT / invoiceMonths.length : 0;
  const topGrade = data?.grades?.[0];

  return (
    <div style={{padding:'24px', maxWidth:'1200px', fontFamily:'system-ui,sans-serif', color:'var(--color-text-primary,#1a1a1a)'}}>
      <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'20px', flewWrap:'wrap', gap:'12px'}}>
        <div>
          <h1 style={{fontSize:'22px', fontWeight:'600', margin:'0 0 4px'}}>Demand Forecast</h1>
          <p style={{fontSize:'13px', color:'#888', margin:0}}>Invoice history + live enquiry pipeline</p>
        </div>
        <div style={{display:'flex', gap:'4px'}}>
          {PERIODS.map(p => (
            <button key={p.value} onClick={() => setPeriod(p.value)} style={{
              padding:'6px 14px', fontSize:'13px', borderRadius:'6px', cursor:'pointer',
              border: period===p.value ? '1px solid #1a1a1a' : '1px solid #e0e0e0',
              background: period===p.value ? '#1a1a1a' : 'transparent',
              color: period===p.value ? '#fff' : '#555',
              fontWeight: period===p.value ? '600' : '400',
            }}>{p.label}</button>
          ))}
        </div>
      </div>

      {/* KPI cards */}
      <div style={{display:'grid', gridTemplateColumns:'repeat(4,minmax(0,1fr))', gap:'12px', marginBottom:'20px'}}>
        {[
          { label:'total shipped', value: loading ? '—' : `${Math.round(totalMT)} MT` },
          { label:'avg / month', value: loading ? '—' : `${Math.round(avgMonthly)} MT` },
          { label:'top grade', value: loading ? '—' : (topGrade?.grade || '—'), sub: topGrade ? `${Math.round(topGrade.total_mt)} MT` : '' },
          { label:'active customers', value: loading ? '—' : (data?.customers?.length || 0) },
        ].map((k,i) => (
          <div key={i} style={{background:'#F8F9FA', borderRadius:'10px', padding:'14px 16px'}}>
            <p style={{fontSize:'12px', color:'#888', margin:'0 0 6px', textTransform:'uppercase', letterSpacing:'0.4px'}}>{k.label}</p>
            <p style={{fontSize:'22px', fontWeight:'600', margin:0, color: i===2 ? getColor(topGrade?.grade) : '#1a1a1a'}}>{k.value}</p>
            {k.sub && <p style={{fontSize:'12px', color:'#888', margin:'2px 0 0'}}>{k.sub}</p>}
          </div>
        ))}
      </div>

      {/* View tabs */}
      <div style={{display:'flex', gap:'4px', marginBottom:'16px', flexWrap:'wrap'}}>
        {VIEWS.map(v => (
          <button key={v.value} onClick={() => setView(v.value)} style={{
            padding:'6px 14px', fontSize:'13px', borderRadius:'6px', cursor:'pointer',
            border:'1px solid #e0e0e0',
            background: view===v.value ? '#F0F0F0' : 'transparent',
            color: view===v.value ? '#1a1a1a' : '#666',
            fontWeight: view===v.value ? '600' : '400',
          }}>{v.label}</button>
        ))}
      </div>

      {loading ? (
        <div style={{textAlign:'center', padding:'60px', color:'#888'}}>Loading...</div>
      ) : (
        <>
          {/* Volume by grade */}
          {view==='volume' && (
            <div style={{background:'white', border:'1px solid #eee', borderRadius:'12px', padding:'20px'}}>
              <p style={{fontSize:'13px', color:'#888', margin:'0 0 16px'}}>Total MT shipped per grade in selected period</p>
              {(data?.grades||[]).map(g => {
                const pct = totalMT > 0 ? (g.total_mt / totalMT * 100) : 0;
                return (
                  <div key={g.grade} style={{marginBottom:'14px'}}>
                    <div style={{display:'flex', justifyContent:'space-between', marginBottom:'4px'}}>
                      <span style={{fontSize:'13px', fontWeight:'600', color: getColor(g.grade)}}>{g.grade}</span>
                      <span style={{fontSize:'13px', color:'#555'}}>{Math.round(g.total_mt)} MT &nbsp;<span style={{color:'#aaa'}}>({Math.round(pct)}%)</span></span>
                    </div>
                    <div style={{background:'#F0F0F0', borderRadius:'4px', height:'8px', overflow:'hidden'}}>
                      <div style={{width:`${pct}%`, height:'100%', background: getColor(g.grade), borderRadius:'4px', transition:'width 0.4s'}}/>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Monthly trend */}
          {view==='trend' && (
            <div style={{background:'white', border:'1px solid #eee', borderRadius:'12px', padding:'20px', overflowX:'auto'}}>
              <p style={{fontSize:'13px', color:'#888', margin:'0 0 16px'}}>MT shipped per grade per month</p>
              <table style={{width:'100%', borderCollapse:'collapse', fontSize:'12px', minWidth:'600px'}}>
                <thead>
                  <tr style={{borderBottom:'2px solid #F0F0F0'}}>
                    <th style={{padding:'8px 12px', textAlign:'left', color:'#666', fontWeight:'500'}}>Grade</th>
                    {invoiceMonths.map(m => (
                      <th key={m} style={{padding:'8px 6px', textAlign:'center', color:'#666', fontWeight:'400', whiteSpace:'nowrap'}}>
                        {m.slice(2).replace('-','/')}
                      </th>
                    ))}
                    <th style={{padding:'8px 12px', textAlign:'center', color:'#444', fontWeight:'600', borderLeft:'2px solid #F0F0F0'}}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {invoiceGrades.map((g, gi) => {
                    const rowTotal = invoiceMonths.reduce((s,m) => s+getInvMT(g,m), 0);
                    const maxInRow = Math.max(...invoiceMonths.map(m => getInvMT(g,m)), 1);
                    return (
                      <tr key={g} style={{borderBottom:'1px solid #F5F5F5', background: gi%2===0?'white':'#FAFAFA'}}>
                        <td style={{padding:'8px 12px', fontWeight:'600', color: getColor(g), whiteSpace:'nowrap'}}>{g}</td>
                        {invoiceMonths.map(m => {
                          const v = getInvMT(g, m);
                          const intensity = v > 0 ? Math.max(0.15, v/maxInRow) : 0;
                          const hexAlpha = Math.round(intensity*200).toString(16).padStart(2,'0');
                          return (
                            <td key={m} style={{padding:'6px 4px', textAlign:'center'}}>
                              {v > 0 ? (
                                <span style={{
                                  display:'inline-flex', alignItems:'center', justifyContent:'center',
                                  minWidth:'38px', height:'26px', borderRadius:'5px', padding:'0 4px',
                                  background: getColor(g)+hexAlpha,
                                  color: intensity > 0.5 ? 'white' : getColor(g),
                                  fontWeight:'600', fontSize:'11px',
                                }}>{Math.round(v)}</span>
                              ) : <span style={{color:'#DDD'}}>—</span>}
                            </td>
                          );
                        })}
                        <td style={{padding:'8px 12px', textAlign:'center', fontWeight:'700', color: getColor(g), borderLeft:'2px solid #F0F0F0'}}>{Math.round(rowTotal)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Customers */}
          {view==='customers' && (
            <div style={{background:'white', border:'1px solid #eee', borderRadius:'12px', padding:'20px'}}>
              <p style={{fontSize:'13px', color:'#888', margin:'0 0 16px'}}>Top customers by MT shipped in selected period</p>
              {(data?.customers||[]).map((c, i) => {
                const pct = totalMT > 0 ? (c.total_mt / totalMT * 100) : 0;
                return (
                  <div key={i} style={{marginBottom:'14px'}}>
                    <div style={{display:'flex', justifyContent:'space-between', marginBottom:'4px'}}>
                      <span style={{fontSize:'13px', fontWeight:'500'}}>{c.customer}</span>
                      <span style={{fontSize:'13px', color:'#555'}}>{Math.round(c.total_mt)} MT &nbsp;<span style={{color:'#aaa'}}>({Math.round(pct)}%)</span></span>
                    </div>
                    <div style={{background:'#F0F0F0', borderRadius:'4px', height:'7px', overflow:'hidden'}}>
                      <div style={{width:`${pct}%`, height:'100%', background:'#1D9E75', borderRadius:'4px', transition:'width 0.4s'}}/>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Enquiry heatmap */}
          {view==='heatmap' && (
            <div style={{background:'white', border:'1px solid #eee', borderRadius:'12px', padding:'20px', overflowX:'auto'}}>
              <p style={{fontSize:'13px', color:'#888', margin:'0 0 16px'}}>Enquiry count per grade per month (from CRM pipeline)</p>
              {(() => {
                const enqData = Array.isArray(data?.enquiry_data) ? data.enquiry_data : [];
                if (enqData.length === 0) return <p style={{color:'#aaa', textAlign:'center', padding:'40px'}}>No enquiry data for this period.</p>;
                const enqMonths = [...new Set(enqData.map(d=>d.month))].sort();
                const enqGrades = Array.from(new Set(enqData.map(d=>d.grade).filter(Boolean))).sort((a,b) => {
                  const ta = enqData.filter(d=>d.grade===a).reduce((s,d)=>s+d.count,0);
                  const tb = enqData.filter(d=>d.grade===b).reduce((s,d)=>s+d.count,0);
                  return tb-ta;
                });
                const maxEnq = Math.max(...enqData.map(d=>d.count), 1);
                return (
                  <table style={{width:'100%', borderCollapse:'collapse', fontSize:'12px', minWidth:'600px'}}>
                    <thead>
                      <tr style={{borderBottom:'2px solid #F0F0F0'}}>
                        <th style={{padding:'8px 12px', textAlign:'left', color:'#666', fontWeight:'500'}}>Grade</th>
                        {enqMonths.map(m => (
                          <th key={m} style={{padding:'8px 6px', textAlign:'center', color:'#666', fontWeight:'400', whiteSpace:'nowrap'}}>
                            {m.slice(2).replace('-','/')}
                          </th>
                        ))}
                        <th style={{padding:'8px 12px', textAlign:'center', color:'#444', fontWeight:'600', borderLeft:'2px solid #F0F0F0'}}>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {enqGrades.map((g,gi) => {
                        const rowTotal = enqMonths.reduce((s,m) => s+getEnqCount(g,m), 0);
                        return (
                          <tr key={g} style={{borderBottom:'1px solid #F5F5F5', background:gi%2===0?'white':'#FAFAFA'}}>
                            <td style={{padding:'8px 12px', fontWeight:'600', color:getColor(g), whiteSpace:'nowrap'}}>{g}</td>
                            {enqMonths.map(m => {
                              const v = getEnqCount(g, m);
                              const intensity = v > 0 ? Math.max(0.15, v/maxEnq) : 0;
                              const hexAlpha = Math.round(intensity*200).toString(16).padStart(2,'0');
                              return (
                                <td key={m} style={{padding:'6px 4px', textAlign:'center', cursor: v>0?'pointer':'default'}}
                                  onClick={() => v>0 && openDetail(g, m)}>
                                  {v > 0 ? (
                                    <span style={{
                                      display:'inline-flex', alignItems:'center', justifyContent:'center',
                                      minWidth:'32px', height:'26px', borderRadius:'5px', padding:'0 4px',
                                      background: getColor(g)+hexAlpha,
                                      color: intensity > 0.5 ? 'white' : getColor(g),
                                      fontWeight:'600', fontSize:'11px',
                                    }}>{v}</span>
                                  ) : <span style={{color:'#DDD'}}>—</span>}
                                </td>
                              );
                            })}
                            <td style={{padding:'8px 12px', textAlign:'center', fontWeight:'700', color:getColor(g), borderLeft:'2px solid #F0F0F0'}}>{rowTotal}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                );
              })()}
              {detailCell && (
                <div style={{marginTop:'16px', borderTop:'1px solid #F0F0F0', paddingTop:'16px'}}>
                  <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'12px'}}>
                    <h3 style={{margin:0, fontSize:'14px'}}>Enquiries — {detailCell.grade} / {detailCell.month}</h3>
                    <button onClick={() => setDetailCell(null)} style={{border:'1px solid #e0e0e0', background:'white', padding:'5px 12px', borderRadius:'6px', cursor:'pointer', fontSize:'12px'}}>Close</button>
                  </div>
                  {detailLoading ? (
                    <p style={{color:'#888'}}>Loading...</p>
                  ) : detailRows.length === 0 ? (
                    <p style={{color:'#888'}}>No enquiries found.</p>
                  ) : (
                    <table style={{width:'100%', borderCollapse:'collapse', fontSize:'12px'}}>
                      <thead>
                        <tr style={{background:'#F8F9FA', borderBottom:'1px solid #E5E7EB'}}>
                          {['Date','Account','Status','Owner','Offer No'].map(h => (
                            <th key={h} style={{padding:'7px 10px', textAlign:'left', fontWeight:'600', color:'#555', fontSize:'11px'}}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {detailRows.map(r => (
                          <tr key={r.id} style={{borderBottom:'1px solid #F1F5F9'}}>
                            <td style={{padding:'7px 10px', color:'#888'}}>{r.enquiry_date?.slice(0,10)||'—'}</td>
                            <td style={{padding:'7px 10px', fontWeight:'600'}}>{r.account_name||'—'}</td>
                            <td style={{padding:'7px 10px'}}>{r.status||'—'}</td>
                            <td style={{padding:'7px 10px'}}>{r.owner_name||r.owner_ecode||'—'}</td>
                            <td style={{padding:'7px 10px'}}>{r.offer_no||'—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
